import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { formatDateLocal } from '../../utils/dateUtils';

interface User {
  id: number;
  username: string;
  email: string;
  role?: string;
}

interface AttendanceEntry {
  id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  date: string;
  checkin: string | null;
  checkout: string | null;
  in_position: string | null;
  out_position: string | null;
  task_hours?: number;
  total_hours?: number;
  log_type?: string;
}

interface WeekViewProps {
  users: User[];
  attendance: AttendanceEntry[];
  loading: boolean;
  canManage?: boolean;
  onEdit?: (entry: AttendanceEntry) => void;
  onDelete?: (id: number, logType?: string) => void;
  onDateRangeChange?: (range: { dateFrom: string; dateTo: string }) => void;
}

interface WeekInfo {
  startDate: Date;
  endDate: Date;
  label: string;
}

export default function WeekView({ 
  users, 
  attendance, 
  loading,
  onDateRangeChange,
}: WeekViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Sort users: by role (System Admin > Sale > Field Worker), then alphabetically
  const sortedUsers = useMemo(() => {
    const rolePriority: Record<string, number> = {
      'SYSTEM ADMIN': 0,
      'SALE': 1,
      'FIELD WORKER': 2,
    };
    return [...users].sort((a, b) => {
      const roleA = rolePriority[a.role?.toUpperCase() || ''] ?? 99;
      const roleB = rolePriority[b.role?.toUpperCase() || ''] ?? 99;
      if (roleA !== roleB) return roleA - roleB;
      return (a.username || a.email).localeCompare(b.username || b.email);
    });
  }, [users]);

  // Get 4 weeks starting from the current week
  const weeks = useMemo(() => {
    const weeksList: WeekInfo[] = [];
    const startDate = new Date(currentDate);
    const day = startDate.getDay();
    // Calculate days to subtract to get to Sunday (week starts on Sunday)
    const diff = startDate.getDate() - day;
    startDate.setDate(diff);
    startDate.setHours(0, 0, 0, 0);

    for (let i = 0; i < 4; i++) {
      const weekStart = new Date(startDate);
      weekStart.setDate(startDate.getDate() + (i * 7));
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      weeksList.push({
        startDate: weekStart,
        endDate: weekEnd,
        label: `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      });
    }
    return weeksList;
  }, [currentDate]);

  // Notify parent of date range change
  useEffect(() => {
    if (onDateRangeChange && weeks.length > 0) {
      const dateFrom = formatDateLocal(new Date(weeks[0].startDate.getTime() - 7 * 24 * 60 * 60 * 1000));
      const dateTo = formatDateLocal(new Date(weeks[weeks.length - 1].endDate.getTime() + 7 * 24 * 60 * 60 * 1000));
      onDateRangeChange({ dateFrom, dateTo });
    }
  }, [weeks, onDateRangeChange]);

  // Create a map of attendance by user_id and date
  const attendanceMap = useMemo(() => {
    const map = new Map<string, AttendanceEntry>();
    attendance.forEach((entry) => {
      if (entry.date && entry.user_id) {
        const key = `${entry.user_id}-${entry.date}`;
        map.set(key, entry);
      }
    });
    return map;
  }, [attendance]);

  // Calculate hours worked for a week
  const calculateWeekHours = (userId: number, week: WeekInfo): { totalHours: number; daysWorked: number; daysPresent: number } => {
    let totalHours = 0;
    let daysWorked = 0;
    let daysPresent = 0;

    for (let i = 0; i < 7; i++) {
      const date = new Date(week.startDate);
      date.setDate(week.startDate.getDate() + i);
      const dateStr = formatDateLocal(date);
      const key = `${userId}-${dateStr}`;
      const entry = attendanceMap.get(key);

      if (entry) {
        if (entry.checkin) {
          daysPresent++;
        }
        if (entry.checkin && entry.checkout) {
          daysWorked++;
          // Use total_hours from backend if available (calculated from individual records)
          // Otherwise fall back to calculating from checkin/checkout
          if (entry.total_hours !== undefined && entry.total_hours !== null) {
            totalHours += entry.total_hours;
          } else {
            try {
              const checkinTime = new Date(entry.checkin);
              const checkoutTime = new Date(entry.checkout);
              const diffMs = checkoutTime.getTime() - checkinTime.getTime();
              const diffHours = diffMs / (1000 * 60 * 60);
              totalHours += diffHours;
            } catch {
              // Ignore invalid dates
            }
          }
        }
      }
    }

    return {
      totalHours: Math.round(totalHours * 100) / 100,
      daysWorked,
      daysPresent,
    };
  };

  // Get week summary chip
  const getWeekChip = (hours: number, daysWorked: number, daysPresent: number) => {
    if (daysWorked === 0 && daysPresent === 0) {
      return <Chip label="-" color="default" size="small" />;
    }
    const label = `${hours.toFixed(1)}h / ${daysWorked}d`;
    return <Chip label={label} color={daysWorked >= 5 ? 'success' : daysWorked > 0 ? 'warning' : 'default'} size="small" />;
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Week Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <IconButton onClick={handlePreviousMonth}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ cursor: 'pointer' }} onClick={handleToday}>
          {weeks[0].startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </Typography>
        <IconButton onClick={handleNextMonth}>
          <ChevronRight />
        </IconButton>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: { xs: 100, sm: 200 }, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'background.paper' }}>
                User
              </TableCell>
              {weeks.map((week) => (
                <TableCell key={week.label} align="center" sx={{ minWidth: 150 }}>
                  <Box>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Week
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {week.label}
                    </Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => {
              const weekStats = weeks.map((week) => calculateWeekHours(user.id, week));
              const totalHours = weekStats.reduce((sum, stat) => sum + stat.totalHours, 0);
              const totalDays = weekStats.reduce((sum, stat) => sum + stat.daysWorked, 0);

              return (
                <TableRow key={user.id} hover>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 2,
                      backgroundColor: 'background.paper',
                      fontWeight: 'medium',
                      maxWidth: { xs: 100, sm: 200 },
                      width: { xs: 100, sm: 200 },
                    }}
                  >
                    <Box sx={{ overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight="medium" noWrap>
                        {user.username || user.email.split('@')[0]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {user.email}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Total: {totalHours.toFixed(1)}h / {totalDays}d
                      </Typography>
                    </Box>
                  </TableCell>
                  {weeks.map((week, idx) => {
                    const stats = weekStats[idx];
                    return (
                      <TableCell key={week.label} align="center">
                        <Tooltip
                          title={`Days worked: ${stats.daysWorked}\nDays present: ${stats.daysPresent}\nTotal hours: ${stats.totalHours.toFixed(1)}h`}
                        >
                          {getWeekChip(stats.totalHours, stats.daysWorked, stats.daysPresent)}
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
